import React, { useEffect, useState } from "react";
import Navbar from "../component/Navbar/NavbarCommon";
import Footer from "../component/Footer/FooterCommon";
import { toast } from "react-toastify";
import { api } from "../api";
import { ResponseTypes } from "../api/types";
import { API_URL, ASSETS_URL, POCKET_BASE_URL } from "../utils/config";
import ArrowButton from "../component/Common/ArrowButton";
import { useCountryStore } from "../store/useCountryStore";

const EmployeeAnniversaryCards: React.FC = () => {
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [anniversaryList, setAnniversaryList] = useState<any[]>([]);
  const { country } = useCountryStore();

  // Fetch employees having anniversary this month
  useEffect(() => {
    fetchAllEmployees();
  }, [country]);

  function sortByDay(arr: any): any {
    return arr.sort((a, b) => {
      const dayA = new Date(a.joiningDate).getDate(); // extract only the day
      const dayB = new Date(b.joiningDate).getDate();
      return dayA - dayB;
    });
  }
  const fetchAllEmployees = async () => {
    try {
      // Pass country parameter to get country-specific data
      const countryParam = country?.name || "India";
      const { data, error } = await api.get<ResponseTypes>(
        `${API_URL.aniversaryMonth}?country=${countryParam}`,
      );
      if (error) {
        toast.error(error.message);
        return;
      }

      if (data?.data) {
        console.log("Anniversary data:", data.data);
        setAnniversaryList(sortByDay(data.data));
      }
    } catch (err) {
      toast.error("Failed to fetch anniversaries");
    }
  };

  // Navbar hide on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (Math.abs(currentScrollY - lastScrollY) < 5) return;
      setIsNavbarVisible(currentScrollY < lastScrollY || currentScrollY < 50);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  // Helper to calculate years completed
  const calculateYears = (dateString: string) => {
    const startYear = new Date(dateString).getFullYear();
    const currentYear = new Date().getFullYear();
    return currentYear - startYear;
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Navbar */}
      <Navbar isVisible={isNavbarVisible} />

      {/* Header */}
      <div className="mt-[140px] px-4 sm:px-6 lg:px-10">
        <ArrowButton />
        <div className="mb-6 sm:mb-8 md:mb-10 text-center sm:text-left my-7">
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-black mb-2 sm:mb-3 leading-tight">
            <span className="text-navgreen">Employee</span> Anniversaries This
            Month
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 font-medium leading-relaxed max-w-4xl">
            Celebrate and acknowledge the employees who have work anniversaries
            this month.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 mt-4 px-4 sm:px-6 lg:px-10 pb-28">
        {anniversaryList.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {anniversaryList.map((emp: any, idx: number) => (
              <div
                key={emp._id || idx}
                className="bg-white rounded-xl shadow-lg p-4 flex flex-col items-center text-center hover:shadow-2xl transition-shadow duration-300"
              >
                <img
                  src={
                    emp?.profilePic
                      ? `${ASSETS_URL}${emp.profilePic}`
                      : emp?.userId
                        ? `${POCKET_BASE_URL}${emp.userId}`
                        : "assets/user-4.svg"
                  }
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "assets/user-4.svg";
                  }}
                  alt={emp.name}
                  className="w-20 h-20 rounded-full mb-3 object-cover"
                />
                <h2 className="font-semibold text-lg text-gray-800">
                  {emp.name}
                </h2>
                <p className="text-gray-500 text-sm mb-2">
                  {emp.designation}{" "}
                  {emp.department ? `- ${emp.department}` : ""}
                </p>
                {emp.joiningDate && (
                  <span className="text-green-600 font-medium">
                    {new Date(emp.joiningDate).toLocaleDateString("en-US", {
                      day: "numeric",
                      month: "short",
                    })}{" "}
                    — {calculateYears(emp.joiningDate)} year
                    {calculateYears(emp.joiningDate) > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 text-lg mt-20">
            No anniversaries this month 🎉
          </p>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default EmployeeAnniversaryCards;
